import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "../components/Head";
import { useAuth } from "../context/AuthContext";
import CompanyReportSnapshot from "../components/CompanyReportSnapshot";
import "./style/landing.css";

const PENDING_ANALYSIS_KEY = "finpilot_pending_analysis_query";
const DEMO_QUERY = "alza.cz a.s.";

const buildSearchParams = (query) => {
  const trimmed = String(query || "").trim();
  const params = new URLSearchParams();
  params.set("query", trimmed);

  if (/^\d{8}$/.test(trimmed)) {
    params.set("analyze", "1");
  } else {
    params.set("search", "1");
  }

  return params.toString();
};

function LandingPage() {
  const [searchInput, setSearchInput] = useState(DEMO_QUERY);
  const [snapshotExpanded, setSnapshotExpanded] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
  }, []);

  const handleAnalyze = (event) => {
event.preventDefault();
  const trimmed = searchInput.trim();
  if (!trimmed) return;

  if (trimmed.toLowerCase() === DEMO_QUERY.toLowerCase()) {
    setSnapshotExpanded(true);
    
    requestAnimationFrame(() => {
      const section = document.getElementById("landing-snapshot");
      if (section) {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
        
        // СБРОСИМ флаг запроса после начала анимации, 
        // чтобы кнопка "скрыть" снова работала корректно
        setTimeout(() => {
          setSnapshotExpanded(false); 
        }, 1000);

        setTimeout(() => {
          window.scrollBy({ top: 120, behavior: "smooth" });
        }, 250);
      }
    });
    return;
  }

    const searchParams = buildSearchParams(trimmed);

    if (user) {
      navigate(`/app/vyhledani-firmy?${searchParams}`);
      return;
    }

    window.localStorage.setItem(PENDING_ANALYSIS_KEY, trimmed);
    navigate("/?auth=register&postAuthAction=analyze");
  };

  return (
    <div className="landing">
      <Helmet>
        <title>FinPilot – Ocenění firem a finanční analýza online</title>
        <meta
          name="description"
          content="FinPilot pomáhá ocenit české firmy, porovnat je s odvětvím a připravit finanční analýzu během minut. Využijte otevřené statistiky a benchmarking zdarma."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://finpilot.cz/" />
        <meta property="og:title" content="FinPilot – Ocenění firem a finanční analýza online" />
        <meta
          property="og:description"
          content="Rychlé ocenění českých firem, benchmarking dle NACE a profesionální finanční analýza na jednom místě."
        />
        <meta property="og:url" content="https://finpilot.cz/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://finpilot.cz/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://finpilot.cz/og-image.png" />
      </Helmet>
      <section className="landing__hero" style={{ minHeight: "80vh", display: "flex", alignItems: "center" }}>
        <div className="hero__content" style={{ maxWidth: "800px", margin: "0 auto", textAlign: "center" }}>
          <p className="hero__badge">🚀 Finanční analýza pro investory a analytiky</p>
          <h1 style={{ fontSize: "3rem", marginBottom: "1.5rem", lineHeight: "1.2" }}>
            Oceňte libovolnou českou firmu <br />
            <span style={{ color: "var(--accent)" }}>během několika sekund</span>
          </h1>
          <p style={{ fontSize: "1.2rem", color: "#666", marginBottom: "2rem" }}>
            Získejte okamžitý přehled o hodnotě firem, finančním zdraví a srovnání s konkurencí.
            Data z účetních výkazů 25 000+ firem.
          </p>

          <form onSubmit={handleAnalyze} className="hero__search-box">
            <input
              type="text"
              placeholder="Zadejte název firmy nebo IČO (např. 24698059)..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className="hero__search-input"
            />
            <button className="button button--primary hero__search-submit" type="submit">Analyzovat firmu</button>
          </form>

          <div className="hero__pills" style={{ justifyContent: "center" }}>
            <span>✅ Ocenění EBITDA multiple</span>
            <span>✅ Oficiální data (Justice.cz)</span>
            <span>✅ Srovnání dle NACE</span>
          </div>
        </div>
      </section>

      <CompanyReportSnapshot
        expandRequested={snapshotExpanded}
        onExpandedChange={setSnapshotExpanded}
      />

      <section id="features" className="landing__section">
        <h2 style={{ textAlign: "center" }}>Profesionální nástroje místo Excelu</h2>
        <div className="cards">
          <article>
            <div style={{ fontSize: "2rem", marginBottom: "10px" }}>📊</div>
            <strong>Okamžité ocenění</strong>
            <p>
              Automatický výpočet hodnoty podniku na základě EBITDA tržních násobků a majetku společnosti.
              Zjistěte férové rozmezí ceny pro nákup či prodej. 
            </p>
          </article>
          <article>
            <div style={{ fontSize: "2rem", marginBottom: "10px" }}>⚖️</div>
            <strong>Hloubkový Benchmarking</strong>
            <p>
              Porovnejte firmu s mediánem odvětví (NACE).
              Je její rentabilita a likvidita lepší než u konkurence?
            </p>
          </article>
          <article>
            <div style={{ fontSize: "2rem", marginBottom: "10px" }}>🧾</div>
            <strong>Oborový přehled průměrů</strong>
            <p>
Zjistěte, jaké jsou typické hodnoty ukazatelů (CAGR, ROA, ROE, zadluženost) pro dané odvětví.
            </p>
          </article>
        </div>
      </section>


      <section id="statistiky" className="landing__section">
        <h2>Otevřená databáze statistik</h2>
        <p>Prozkoumejte trendy české ekonomiky. Nabízíme agregovaná data zdarma pro veřejnost.</p>

        <div className="cards">
          <article style={{ cursor: "pointer" }} onClick={() => navigate("/oborove-statistiky")}>
            <strong>📈 Detailní analýza odvětví</strong>
            <p>
              Vyberte sekci NACE (např. Výroba, IT, Stavebnictví) a zobrazte vývoj marží,
              růstu tržeb (CAGR) a indexy aktivity za posledních 8 let.
            </p>
            <span style={{ color: "var(--accent)", fontWeight: "bold", marginTop: "10px", display: "block" }}>Přejít na statistiky →</span>
          </article>

          <article style={{ cursor: "pointer" }} onClick={() => navigate("/benchmark-obory")}>
            <strong>🏆 Benchmark oborů</strong>
            <p>
              Interaktivní srovnání sektorů. Kde je nejvyšší rentabilita aktiv?
              Které obory jsou nejvíce zadlužené?
            </p>
            <span style={{ color: "var(--accent)", fontWeight: "bold", marginTop: "10px", display: "block" }}>Přejít na benchmark →</span>
          </article>
        </div>
      </section>

      <section id="about" className="landing__section" style={{ borderTop: "1px solid #eee" }}>
        <h2>O projektu a metodice</h2>
        <div className="cards">
          <article>
            <strong>Transparentní metodika</strong>
            <p>
              U každého ukazatele (Likvidita, Rentabilita, Zadluženost)
              vidíte přesný vzorec, jak byl vypočítán z účetních výkazů.
            </p>
          </article>
          <article>
            <strong>Garant odbornosti</strong>
            <p>
              Autorem metodiky je <strong>Ing. Ivan Bázin</strong>, investiční analytik (Genesis Capital), dříve M&amp;A analytik
              a advisor v poradenské společnosti Galik In. Algoritmy vycházejí z praxe oceňování podniků.{" "}
              <Link to="/o-projektu" style={{ color: "var(--accent)" }}>O projektu a metodice</Link>
            </p>
            <p style={{ fontSize: "0.9rem", marginTop: "10px" }}>
              📧 <a href="mailto:info@finpilot.cz" style={{ color: "var(--accent)" }}>info@finpilot.cz</a>
            </p>
          </article>
        </div>
    </section>

      <section className="landing__section" id="faq" style={{ borderTop: "1px solid #eee" }}>
        <h2>FAQ: Ocenění firem a oborové statistiky</h2>
        <div className="cards">
          <article>
            <strong>Jak rychle získám první výstupy?</strong>
            <p>
              Po zadání názvu firmy nebo IČO získáte základní přehled během několika sekund.
              Pro hlubší porovnání doporučujeme navázat přes
              {" "}<Link to="/oborove-statistiky">detailní analýzu odvětví</Link>{" "}
              a{" "}<Link to="/benchmark-obory">benchmark oborů</Link>.
            </p>
          </article>
          <article>
            <strong>Jsou statistiky veřejně dostupné?</strong>
            <p>
              Ano, otevřené pohledy na oborové statistiky i benchmark můžete procházet zdarma
              bez registrace.
            </p>
          </article>
          <article>
            <strong>Jak FinPilot pomůže při rozhodování?</strong>
            <p>
              Kombinací ocenění, trendů a benchmarku získáte rychlejší podklad pro investiční
              i manažerská rozhodnutí. Pokud chcete vlastní report, pokračujte do
              {" "}<Link to="/app/vyhledani-firmy">modulu vyhledání firmy</Link>.
            </p>
          </article>
        </div>
      </section>

      <section className="landing__section">
        <div className="cta" style={{ textAlign: "center" }}>
          <div className="cta__card" >
            <h3>Začněte s analýzou hned teď</h3>
            <p>
              Registrace je zdarma a zabere méně než minutu. Získáte přístup k detailním reportům.
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "20px" }}>
              <button className="button button--primary" onClick={() => navigate("/app/vyhledani-firmy")}>
                Vytvořit účet zdarma
              </button>
              <button className="button button--white" onClick={() => navigate("/benchmark-obory")}>
                Prozkoumat statistiky odvětví
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default LandingPage;
